/*
 * MCRO001-F0050 / T-MCRO-050 (design: choreo MCRO001-D004 §§3/4.1.2/4.1.3, commit 84538abd)
 * Slice 1 of 3 — pure 3-way line merge primitive.
 *
 * GENERIC, dependency-light: the only import is `node-diff3` (offline-vendored).
 * No choreography types cross this boundary. Reusable for any line-oriented text.
 *
 * PURITY CONTRACT (mutation-provable): merge3 is a pure function of its string
 * inputs. Same (base, ours, theirs, opts) -> byte-identical `merged` and identical
 * conflict ordering. No clock, no randomness, no I/O. See merge3.test.js.
 */

import { diff3Merge, diffComm } from 'node-diff3';

/**
 * @typedef {Object} ConflictNode
 * A structured conflict region (NOT an in-band marker string). Line arrays are the
 * exact lines (terminators preserved) for each side of an overlapping hunk.
 * @property {string} kind        Always 'conflict' (well-known kind, §4.2).
 * @property {string[]} base      Ancestor lines for this region (empty in 2-way degrade).
 * @property {string[]} ours      Our side's lines.
 * @property {string[]} theirs    Their side's lines.
 */

/**
 * @typedef {Object} Merge3Result
 * @property {boolean} clean          === (conflicts.length === 0)
 * @property {string} merged          Auto-merged text; conflict regions carry ours+theirs
 *                                    verbatim (caller serializes them into AST boxes).
 * @property {ConflictNode[]} conflicts
 * @property {boolean} degraded       true when this was a 2-way (no usable base) merge.
 */

/**
 * Split text into an array of lines WITH their terminators preserved, so a mixed
 * CRLF/LF/CR document round-trips exactly (opts.eol: 'preserve'). The array joins
 * back to the original bytes with `parts.join('')`. A trailing newline yields a final
 * empty-string element (which joins to nothing), so no phantom line is introduced.
 *
 * PURE: deterministic function of `text` only.
 * @param {string} text
 * @returns {string[]}
 */
export function splitLinesKeepEol(text) {
  if (text === '') return [''];
  const parts = [];
  const re = /(\r\n|\r|\n)/g;
  let last = 0;
  let match;
  while ((match = re.exec(text)) !== null) {
    parts.push(text.slice(last, match.index) + match[0]);
    last = re.lastIndex;
  }
  parts.push(text.slice(last)); // trailing remainder (possibly '')
  return parts;
}

/**
 * Inverse of splitLinesKeepEol. PURE.
 * @param {string[]} lines
 * @returns {string}
 */
export function joinLinesKeepEol(lines) {
  return lines.join('');
}

/**
 * 2-way degrade: no usable common ancestor. We cannot tell whose change is new, so
 * EVERY textual difference becomes a conflict region (base always empty) and NOTHING
 * is auto-merged. Common runs (equal on both sides) pass through as merged text.
 *
 * Uses node-diff3 `diffComm` (the classic `comm`-style 3-column diff) so the pairing
 * is deterministic and library-backed rather than hand-rolled.
 *
 * @param {string} ours
 * @param {string} theirs
 * @returns {Merge3Result}
 */
export function merge2(ours, theirs) {
  const oursLines = splitLinesKeepEol(ours);
  const theirsLines = splitLinesKeepEol(theirs);
  const comm = diffComm(oursLines, theirsLines);

  const conflicts = [];
  const mergedParts = [];
  for (const region of comm) {
    if (region.common) {
      // Identical on both sides -> passes through untouched.
      mergedParts.push(...region.common);
    } else {
      const oursRegion = region.buffer1 || [];
      const theirsRegion = region.buffer2 || [];
      // A pure-common region already handled above; here at least one side differs.
      conflicts.push({
        kind: 'conflict',
        base: [],
        ours: oursRegion,
        theirs: theirsRegion,
      });
      // Deterministic placement: ours-then-theirs, so `merged` stays byte-stable.
      mergedParts.push(...oursRegion, ...theirsRegion);
    }
  }

  return {
    clean: conflicts.length === 0,
    merged: joinLinesKeepEol(mergedParts),
    conflicts,
    degraded: true,
  };
}

/**
 * 3-way line merge.
 *
 * Non-overlapping hunks auto-merge; overlapping edits become ConflictNodes.
 * `clean === (conflicts.length === 0)`.
 *
 * Degrade-to-2-way: pass `base === null` (or `undefined`) to signal "no usable base";
 * this routes to {@link merge2} and every diff becomes a conflict (§3.4). An empty
 * string base ('') is a VALID base (empty ancestor), NOT a degrade signal.
 *
 * @param {string|null|undefined} base
 * @param {string} ours
 * @param {string} theirs
 * @param {{ diff?: 'myers'|'patience', eol?: 'preserve' }} [opts]
 * @returns {Merge3Result}
 */
export function merge3(base, ours, theirs, opts = {}) {
  // Degrade path: no usable ancestor supplied.
  if (base === null || base === undefined) {
    return merge2(ours, theirs);
  }

  const diff = opts.diff || 'myers';
  if (diff !== 'myers' && diff !== 'patience') {
    throw new Error(`merge3: unknown diff mode '${diff}' (expected 'myers' | 'patience').`);
  }

  const baseLines = splitLinesKeepEol(base);
  const oursLines = splitLinesKeepEol(ours);
  const theirsLines = splitLinesKeepEol(theirs);

  // node-diff3 order is (a=ours, o=base, b=theirs). `excludeFalseConflicts: true`
  // (default) already collapses "both sides made the identical change" into an `ok`
  // region. Patience is applied as a post-alignment refinement of conflict bounds only
  // (see refineConflictWithPatience) — it never changes auto-merged bytes, matching the
  // design's "merged bytes equivalent when clean; Patience only affects region bounds".
  const regions = diff3Merge(oursLines, baseLines, theirsLines);

  const conflicts = [];
  const mergedParts = [];
  for (const region of regions) {
    if (region.ok) {
      mergedParts.push(...region.ok);
      continue;
    }
    const c = region.conflict;
    let node = {
      kind: 'conflict',
      base: c.o || [],
      ours: c.a || [],
      theirs: c.b || [],
    };
    if (diff === 'patience') {
      node = refineConflictWithPatience(node);
    }
    conflicts.push(node);
    // Placement in `merged`: ours-then-theirs, deterministic.
    mergedParts.push(...node.ours, ...node.theirs);
  }

  return {
    clean: conflicts.length === 0,
    merged: joinLinesKeepEol(mergedParts),
    conflicts,
    degraded: false,
  };
}

/**
 * Patience refinement (behind opts.diff === 'patience'): trim shared prefix/suffix
 * lines out of a conflict region so the reported hunk is bounded to the genuinely
 * divergent lines, improving conflict-region readability. This shrinks a conflict's
 * reported bounds; it does NOT change which regions auto-merge, and does NOT alter the
 * merged byte stream (the shared lines that get trimmed are identical on both sides and
 * were already emitted ours-then-theirs, so trimming is presentation-only metadata on
 * the ConflictNode). Trimmed shared lines are folded into `base` context is out of
 * scope for v1; here we simply tighten ours/theirs to the divergent core.
 *
 * PURE.
 * @param {ConflictNode} node
 * @returns {ConflictNode}
 */
export function refineConflictWithPatience(node) {
  const ours = [...node.ours];
  const theirs = [...node.theirs];

  let prefix = 0;
  while (prefix < ours.length && prefix < theirs.length && ours[prefix] === theirs[prefix]) {
    prefix += 1;
  }
  let suffix = 0;
  while (
    suffix < ours.length - prefix &&
    suffix < theirs.length - prefix &&
    ours[ours.length - 1 - suffix] === theirs[theirs.length - 1 - suffix]
  ) {
    suffix += 1;
  }

  if (prefix === 0 && suffix === 0) return node;

  return {
    kind: node.kind,
    base: node.base,
    ours: ours.slice(prefix, ours.length - suffix),
    theirs: theirs.slice(prefix, theirs.length - suffix),
  };
}
